import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { FILE_EXTRACTION_STATUS } from "../../stores/work_orders";

const hasDetails = ({ details }) =>
  Boolean(
    details?.task ||
      details?.material?.length ||
      details?.services?.length ||
      details?.hints?.length ||
      details?.reference_number,
  );

function DetailText({ label, text }) {
  if (!text) return null;

  return (
    <div className="mb-2">
      <div className="text-muted small">{label}</div>
      <div>{text}</div>
    </div>
  );
}

DetailText.propTypes = {
  label: PropTypes.string.isRequired,
  text: PropTypes.string,
};

function DetailList({ label, items = [] }) {
  if (items.length === 0) return null;

  return (
    <div className="mb-2">
      <div className="text-muted small">{label}</div>
      <ul className="mb-0 ps-3">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

DetailList.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string),
};

function DocumentDetails({ extraction, showSource }) {
  const { t } = useTranslation();
  const { details } = extraction;
  const fileUrl = `/v1/files/${extraction.file_id}/content`;

  return (
    <div>
      {showSource && (
        <div className="mb-2">
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="small text-muted">
            <i className="fa fa-file-pdf fa-fw me-1" />
            {extraction.file_name}
          </a>
        </div>
      )}
      {extraction.from_images && (
        <div className="alert alert-warning small py-2 mb-2">
          <i className="fa fa-image me-2" />
          {t("work_orders.document_details.from_images")}
        </div>
      )}
      <DetailText label={t("work_orders.document_details.task")} text={details.task} />
      <DetailList label={t("work_orders.document_details.material")} items={details.material} />
      <DetailList label={t("work_orders.document_details.services")} items={details.services} />
      <DetailList label={t("work_orders.document_details.hints")} items={details.hints} />
      <DetailText label={t("work_orders.document_details.reference")} text={details.reference_number} />
    </div>
  );
}

const extractionShape = PropTypes.shape({
  file_id: PropTypes.number.isRequired,
  file_name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(FILE_EXTRACTION_STATUS)).isRequired,
  from_images: PropTypes.bool,
  details: PropTypes.shape({
    task: PropTypes.string,
    material: PropTypes.arrayOf(PropTypes.string),
    services: PropTypes.arrayOf(PropTypes.string),
    hints: PropTypes.arrayOf(PropTypes.string),
    reference_number: PropTypes.string,
  }),
});

DocumentDetails.propTypes = {
  extraction: extractionShape.isRequired,
  showSource: PropTypes.bool.isRequired,
};

function DocumentDetailsCard({ extractions, showFailures }) {
  const { t } = useTranslation();

  const withDetails = extractions.filter(
    (extraction) => extraction.status === FILE_EXTRACTION_STATUS.DONE && hasDetails(extraction),
  );
  const isPending = extractions.some((extraction) => extraction.status === FILE_EXTRACTION_STATUS.PENDING);
  const hasFailures =
    showFailures && extractions.some((extraction) => extraction.status === FILE_EXTRACTION_STATUS.FAILED);
  if (withDetails.length === 0 && !isPending && !hasFailures) return null;

  const showSource = withDetails.length > 1;
  const statusLineClass = `text-muted small${withDetails.length > 0 ? " mt-2" : ""}`;

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className="fa fa-wand-magic-sparkles fa-lg fa-fw text-dark text-opacity-50 me-1" />
        {t("work_orders.document_details.title")}
      </div>
      <div className="card-body">
        {withDetails.map((extraction, index) => (
          <div key={extraction.file_id} className={index > 0 ? "border-top pt-3 mt-3" : ""}>
            <DocumentDetails extraction={extraction} showSource={showSource} />
          </div>
        ))}
        {isPending && (
          <div className={statusLineClass}>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            {t("work_orders.document_details.pending")}
          </div>
        )}
        {hasFailures && <div className={statusLineClass}>{t("work_orders.document_details.failed")}</div>}
      </div>
    </div>
  );
}

DocumentDetailsCard.propTypes = {
  extractions: PropTypes.arrayOf(extractionShape).isRequired,
  showFailures: PropTypes.bool.isRequired,
};

export default DocumentDetailsCard;
